import { useRef, useState, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { useGeolocated } from "react-geolocated";
import piexif from "piexifjs";
import { isMobile } from "react-device-detect";

import { Icon, Snackbar, Alert, Typography, Card } from "@mui/material";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

function GPSCameraPage() {
  const pageTitle = "GPS Camera";
  usePageTitle(pageTitle);

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
    setSeverity("error");
  };

  const webcamRef = useRef(); // create a webcam reference
  const [imgSrc, setImgSrc] = useState(null);
  const [cameraUsed, setCameraUsed] = useState(false);

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  async function getGeocodeData(lat, lng) {
      const apiKey = AppConfig.google_map_key;
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
  
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      let formatted_address = null;
      if (data.results.length > 0) {
        formatted_address = data.results[0].formatted_address;
      }
  
      return formatted_address;
    }

  async function addWatermark(base64, text) {
    // Load image as Promise
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = base64;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw image
    ctx.drawImage(img, 0, 0);

    // Settings
    const fontSize = Math.round(img.width * (isMobile ? 0.05 : 0.03)); // ~3% of image width
    const lineHeight = fontSize * 1.3;
    ctx.font = `${fontSize}px Arial`;
    ctx.textBaseline = "top";

    // Split text into lines and wrap long ones
    const maxWidth = img.width * 0.9; // 90% of image width
    const paragraphs = text.split(/\r?\n/);
    const wrappedLines = [];

    for (const para of paragraphs) {
      const words = para.split(" ");
      let line = "";

      for (const word of words) {
        const testLine = line ? `${line} ${word}` : word;
        const testWidth = ctx.measureText(testLine).width;
        if (testWidth > maxWidth) {
          wrappedLines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }
      if (line) wrappedLines.push(line);
    }

    // Measure text box
    const textWidths = wrappedLines.map((l) => ctx.measureText(l).width);
    const textWidth = Math.max(...textWidths);
    const textHeight = wrappedLines.length * lineHeight;

    // Padding and position (bottom-right)
    const padding = Math.round(fontSize * 0.4);
    const x = img.width - textWidth - padding * 2;
    const y = img.height - textHeight - padding * 2;

    // Background box
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(
      x - padding,
      y - padding,
      textWidth + padding * 2,
      textHeight + padding * 2
    );

    // Text color and draw
    ctx.fillStyle = "#fff";
    wrappedLines.forEach((line, i) => {
      ctx.fillText(line, x, y + i * lineHeight);
    });

    // Return JPEG
    return canvas.toDataURL("image/jpeg", 0.92);
  }

  async function geoTagImage(imageSrc, lat, lng, meta) {
    if (!imageSrc) return;

    var m = 1000000;
    var xlat = lat * m;
    var xlng = lng * m;
    var ts = new Date().toISOString();

    var formatted_address = await getGeocodeData(lat, lng);
    var faddr = formatted_address ? `\n${formatted_address}` : "";
    var gpsText = `GeoTagged Image\n${faddr}\n${lat}, ${lng}\n${ts}\nLaddar Africa`;
    var watermarkedImg = await addWatermark(imageSrc, gpsText);

    const zeroth = {};
    zeroth[piexif.ImageIFD.Make] = "Laddar.Africa";
    zeroth[piexif.ImageIFD.Model] = "Laddar Forms";
    zeroth[piexif.ImageIFD.Software] = "GPS_CAMERA";

    const gps = {};
    gps[piexif.GPSIFD.GPSLatitudeRef] = lat > 0 ? "N" : "S";
    gps[piexif.GPSIFD.GPSLatitude] = [xlat, m];
    gps[piexif.GPSIFD.GPSLongitudeRef] = lng > 0 ? "E" : "W";
    gps[piexif.GPSIFD.GPSLongitude] = [xlng, m];

    const exif = {};
    exif[piexif.ExifIFD.DateTimeOriginal] = ts;
    exif[piexif.ExifIFD.UserComment] = meta;
    // zeroth[piexif.ImageIFD.ImageDescription] = "React Custom Metadata";

    // Combine EXIF data
    const exifObj = { "0th": zeroth, Exif: exif, GPS: gps };
    const exifStr = piexif.dump(exifObj);

    // Insert EXIF into image
    const newImage = piexif.insert(exifStr, watermarkedImg);
    return newImage;
  }

  // create a capture function
  const capture = useCallback(
    async (gpsCoords) => {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        setSeverity("error");
        setErrMsg("No photo image found.");
        setOpen(true);
        return;
      }

      var taggedImgSrc = await geoTagImage(
        imageSrc,
        parseFloat(gpsCoords.latitude).toFixed(6),
        parseFloat(gpsCoords.longitude).toFixed(6),
        "Captured using Laddar Africa Data Forms. https://laddar.africa/"
      );
      if (!taggedImgSrc) {
        setSeverity("error");
        setErrMsg(
          "Geo-tagging failed. Enable required browser perimssions and try again."
        );
        setOpen(true);
        return;
      }
      setImgSrc(taggedImgSrc);
    },
    [webcamRef]
  );

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
        // maximumAge: 0,
      },
      // watchPosition: true,
      userDecisionTimeout: 5000,
    });

  function getGPSCoords() {
    if (!isGeolocationAvailable) {
      return false;
    }

    if (!isGeolocationEnabled) {
      return false;
    }

    if (coords) {
      return { latitude: coords.latitude, longitude: coords.longitude };
    }
    return false;
  }

  function downloadImage() {
    var ts = new Date().toISOString();
    const a = document.createElement("a");
    a.href = imgSrc;
    a.download = `geo_tagged_image_laddar_africa_${ts}.jpg`;
    a.click();

    setSeverity("success");
    setErrMsg("Image downloaded successfully!");
    setOpen(true);
    return;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mb={10}>
        <MDBox m={2} display="flex" justifyContent="space-between">
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" align="center">
              GPS Camera
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="h6">
              <Icon color="dark">camera_alt</Icon>
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox mt={3}>
          <MDBox mt={1} mb={3}>
            <Card>
              <MDBox p={2}>
                {imgSrc ? (
                  <MDBox>
                    <img src={imgSrc} alt="webcam" width="100%" />
                    <MDBox align="center">
                      <Typography
                        mt={0}
                        variant="subtitle2"
                        fontSize={14}
                        fontWeight="regular"
                      >
                        <small>
                          {coords.latitude.toFixed(6)},{" "}
                          {coords.longitude.toFixed(6)}
                        </small>
                      </Typography>
                      <Typography
                        mt={0}
                        variant="subtitle2"
                        fontSize={14}
                        fontWeight="light"
                      >
                        <small>
                          <a
                            rel="noreferrer"
                            target="_blank"
                            href={`https://maps.google.com?q=${coords.latitude},${coords.longitude}`}
                          >
                            [View On Map]
                          </a>
                        </small>
                      </Typography>
                    </MDBox>
                  </MDBox>
                ) : (
                  <Webcam
                    width="100%"
                    ref={webcamRef}
                    screenshotQuality={1}
                    videoConstraints={videoConstraints}
                    screenshotFormat="image/jpeg"
                    mirrored={cameraUsed ? true : false}
                  />
                )}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
        <MDBox>
          <Card>
            <MDBox p={2}>
              {imgSrc ? (
                <MDBox
                  align="center"
                  display="flex"
                  justifyContent="space-evenly"
                >
                  <MDButton
                    onClick={() => {
                      setImgSrc(null);
                    }}
                    color="error"
                    variant="outlined"
                    startIcon={<Icon>clear</Icon>}
                    size="small"
                  >
                    Clear
                  </MDButton>
                  &nbsp;
                  <MDButton
                    color="dark"
                    variant="contained"
                    onClick={() => {
                      downloadImage();
                    }}
                    startIcon={<Icon>download</Icon>}
                    size="small"
                  >
                    Save
                  </MDButton>
                </MDBox>
              ) : (
                <MDBox
                  align="center"
                  display="flex"
                  justifyContent="space-evenly"
                >
                  <MDButton
                    onClick={() => {
                      setCameraUsed(!cameraUsed);
                    }}
                    color="warning"
                    variant="outlined"
                    startIcon={<Icon>cameraswitch</Icon>}
                    size="small"
                  >
                    Switch Camera
                  </MDButton>
                  &nbsp;
                  <MDButton
                    color="info"
                    variant="outlined"
                    onClick={() => {
                      const gpsCoords = getGPSCoords();
                      if (gpsCoords === false) {
                        setErrMsg(
                          "Unable to retrieve your current location. Make sure Location permissions are allowed on your browser."
                        );
                        setSeverity("error");
                        setOpen(true);
                        return;
                      }
                      capture(gpsCoords);
                    }}
                    startIcon={<Icon>photo_camera</Icon>}
                    size="small"
                  >
                    Capture
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default GPSCameraPage;
